import { FieldSet, Loading, useApiEffect } from "nsitools-react";
import * as React from "react";
import { Tree, TreeNodeDatum } from "react-d3-tree";
import styled from "styled-components";

import { BroodRequestApi, EBroodRequestDogType, EDogSex, EPlatform } from "../../../../api";
import { AddButton } from "../../../../components";
import { DrupalFieldSet } from "../../../../drupal/common/DurpalFieldSet";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useTl, useWindow } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { HierarchyPointNode } from "d3-hierarchy";
import { SyntheticEvent } from "react";
import * as uuid from "uuid";
import { renderElement } from "../../../../components/genealogyD3TreeUtils.tsx";

const height = 780;

const Container = styled.div`
  border: solid 1px rgba(16, 22, 26, 0.15);

  /* TODO: change this to 100% when pagebase is fixed */
  height: ${height}px;
`;

export interface IPuppyGenealogyPageProps {
  broodRequestId: number;
  platform?: EPlatform;
}

export const PuppyGenealogyPage: React.FunctionComponent<IPuppyGenealogyPageProps> = ({
  broodRequestId,
  platform = EPlatform.CARE4DOGS,
}) => {
  const { t } = useTl();
  const { openNewTab } = useWindow();
  const api = useApiService(BroodRequestApi);
  const [generation, setgeneration] = React.useState<number>(3);
  const [dogData, dogDataLoading] = useApiEffect(
    () => api.getPuppyFutureGenealogy(broodRequestId, generation),
    [generation]
  );

  const _transformToHierarchy = React.useCallback(
    (links, attributeFields) => {
      const nodesByName = {};

      const assignNode = (id) => {
        //console.log("Assign node:", id);
        if (!nodesByName[id]) {
          nodesByName[id] = { id };
        }
        //console.log("Node by name return:", nodesByName[id]);
        return nodesByName[id];
      };

      const assignNodeWithAttributes = (id, name, attributes) => {
        //console.log("Assign node with attributes:", id, name, attributes);
        if (!nodesByName[id]) {
          nodesByName[id] = {
            name,
            attributes,
          };
          nodesByName[id].id = id;
        }
        //console.log("Node by name with attributes return:", nodesByName[id]);
        return nodesByName[id];
      };

      // Create nodes for each unique source and target.
      links.forEach((link) => {
        console.log("link", link);
        // if `attributeFields` is defined, create/overwrite current `link.attributes`
        if (attributeFields) {
          const customAttributes = {};
          attributeFields.forEach((field) => {
            let f = link[field];
            if (f == null) {
            } else {
              if (f instanceof Date) {
                f = f.toLocaleDateString();
              }
              let attrName = field;
              if (field === "breedName") {
                attrName = t(ETLCodes.BreedName);
              } else if (field === "pedigree") {
                attrName = t(ETLCodes.Pedigree);
              } else if (field === "birthDate") {
                attrName = t(ETLCodes.BirthDate);
              } else if (field === "deathDate") {
                attrName = t(ETLCodes.DeathDate);
              }
              customAttributes[attrName] = "" + f;
            }
          });
          link.attributes = customAttributes;
        }

        if (link.name === null) {
          if (link.dogType === EBroodRequestDogType.Puppy) {
            link.name = t(ETLCodes.Puppy);
          } else if (link.dogType === EBroodRequestDogType.Father) {
            link.name = t(ETLCodes.UnknownFather);
          } else {
            link.name = t(ETLCodes.UnknownMother);
          }
        }

        link.source = link.parent ? assignNode(link.parent) : null;
        link.target = assignNodeWithAttributes(link.child, link.name, link.attributes);

        if (link.dogType === EBroodRequestDogType.Puppy) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 180,
                height: 18,
                y: -18,
                x: 3,
                fill: "#EEEEEE",
                stroke: "#DADADA",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        } else if (link.sex === EDogSex.Female) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 240,
                height: 67,
                y: -18,
                x: 3,
                fill: "#FFD6E8",
                stroke: "#BF96A8",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        } else if (link.sex === EDogSex.Male) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 240,
                height: 67,
                y: -18,
                x: 3,
                fill: "#D0E2FF",
                stroke: "#90A2BF",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        } else if (link.dogType === EBroodRequestDogType.Mother) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 180,
                height: 18,
                y: -18,
                x: 0,
                fill: "#EEEEEE",
                stroke: "#BF96A8",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        } else if (link.dogType === EBroodRequestDogType.Father) {
          link.target.renderCustomNodeElement = (nodeProps) => {
            return renderElement(
              "rect",
              {
                width: 180,
                height: 18,
                y: -18,
                x: 0,
                fill: "#EEEEEE",
                stroke: "#90A2BF",
                strokeWidth: 2,
              },
              nodeProps
            );
          };
        }

        const parent = link.source;
        const child = link.target;

        child.dog = link;
        // child.parent = parent.name || null;
        if (parent) {
          // parent.id = link.parentNodeId;
          parent.collapsed = child.collapsed = false; // eslint-disable-line
          // NOTE We assign to a custom `_children` field instead of D3's reserved
          // `children` to avoid update anomalies when collapsing/re-expanding nodes.
          parent.children ? parent.children.push(child) : (parent.children = [child]);
        }
      });

      // Extract & return the root node
      const rootLinks = links.filter((link) => !link.source);
      const rootNode = [rootLinks[0].target];
      return rootNode;
    },
    [t]
  );

  const treeData = React.useCallback(
    (index) => {
      if (dogData) {
        const attributeFields = ["breedName", "pedigree", "birthDate", "deathDate"];
        console.log("SentData:", dogData[index].genealogyList);
        const result = _transformToHierarchy(dogData[index].genealogyList, attributeFields);
        console.log("Result:", result);
        return result;
      } else {
        const empty = [];
        return empty;
      }
    },
    [_transformToHierarchy, dogData]
  );

  const onClickFunction = React.useCallback(
    (source: HierarchyPointNode<TreeNodeDatum>, event: SyntheticEvent) => {
      const dog = (source.data as any).dog;
      if (dog != null) {
        if (dog.dogId > 0) {
          // history.push(`${ERoutes.dog}/${source.dogId}/detail/view`);
          openNewTab(`${ERoutes.dog}/${dog.dogId}/detail/view`);
        }
      }
    },
    [openNewTab]
  );

  const onLinkClickFunction = React.useCallback((linkSource, linkTarget, evt) => {
    console.log(evt);
    console.log(linkSource);
    console.log(linkTarget);
  }, []);

  function drawStepPath(linkData, orientation) {
    const { source, target } = linkData;
    const deltaY = target.y - source.y - 32; // -32 to starts further left from the right edge

    let draw = `M${source.y},${source.x} H${source.y + deltaY} V${target.x} H${target.y}`;
    return draw;
  }

  return (
    <Loading isLoading={dogDataLoading}>
      {dogData?.map((e, index, key) => {
        return (
          <div>
            {platform === EPlatform.CARE4DOGS ? (
              <FieldSet title={dogData[index].fatherName} key={index}>
                <Container>
                  <AddButton text={t(ETLCodes.LoadMore)} onClick={() => setgeneration(generation + 1)} />
                  {
                    <Tree
                      data={treeData(index)}
                      orientation="horizontal"
                      renderCustomNodeElement={(nodeProps) => {
                        if ((nodeProps.nodeDatum as any).renderCustomNodeElement)
                          return (nodeProps.nodeDatum as any).renderCustomNodeElement(nodeProps);
                        return renderElement(
                          "rect",
                          {
                            width: 240,
                            height: 20,
                            y: -20,
                            x: 0,
                            fill: "lightgray",
                            stroke: "gray",
                            strokeWidth: 2,
                          },
                          nodeProps
                        );
                      }}
                      translate={{ x: 8, y: height / 2 }}
                      nodeSize={{
                        x: 288,
                        y: 136,
                      }} // box is 240 by 67, 288 to make some gap between boxes along X axis, 140 is two times the height plus extra gap
                      separation={{ siblings: 1, nonSiblings: 1 }}
                      collapsible={false} // clicking on a defined node opens detail's dog page, cliking on an undefined node opens dialog
                      onNodeClick={onClickFunction}
                      onLinkClick={onLinkClickFunction} // not used
                      pathFunc={drawStepPath}
                      zoom={0.7} // adapt first zoom level used for display, 0.7 to allow displaying 4 levels (to validate on target dispplay size)
                      transitionDuration={0}
                    />
                  }
                </Container>
              </FieldSet>
            ) : (
              <DrupalFieldSet title={dogData[index].fatherName} key={index} collapsable>
                <Container>
                  <AddButton text={t(ETLCodes.LoadMore)} onClick={() => setgeneration(generation + 1)} />
                  {
                    <Tree
                      data={treeData(index)}
                      orientation="horizontal"
                      renderCustomNodeElement={(nodeProps) => {
                        if ((nodeProps.nodeDatum as any).renderCustomNodeElement)
                          return (nodeProps.nodeDatum as any).renderCustomNodeElement(nodeProps);
                        return renderElement(
                          "rect",
                          {
                            width: 240,
                            height: 20,
                            y: -20,
                            x: 0,
                            fill: "lightgray",
                            stroke: "gray",
                            strokeWidth: 2,
                          },
                          nodeProps
                        );
                      }}
                      translate={{ x: 8, y: height / 2 }}
                      nodeSize={{
                        x: 288,
                        y: 136,
                      }} // box is 240 by 67, 288 to make some gap between boxes along X axis, 140 is two times the height plus extra gap
                      separation={{ siblings: 1, nonSiblings: 1 }}
                      collapsible={false} // clicking on a defined node opens detail's dog page, cliking on an undefined node opens dialog
                      onNodeClick={onClickFunction}
                      onLinkClick={onLinkClickFunction} // not used
                      pathFunc={drawStepPath}
                      zoom={0.7} // adapt first zoom level used for display, 0.7 to allow displaying 4 levels (to validate on target dispplay size)
                      transitionDuration={0}
                    />
                  }
                </Container>
              </DrupalFieldSet>
            )}
          </div>
        );
      })}
    </Loading>
  );
};
